// Today's pre-shift briefing. Staff read it and tap "Got it". Managers draft it with AI, edit every part, and publish.
import { useEffect, useState } from 'react';
import { Megaphone, Sparkles, Loader2, Pencil, Check, Eye, Sun, CloudRain, Cloud, Users, AlertTriangle, PackageX, CalendarDays, LayoutGrid, TrendingUp, StickyNote } from 'lucide-react';
import { useAppColors } from '@/src/lib/theme';
import { useBriefing, BRIEFING_FIELDS, type BriefingKey } from '@/src/hooks/useBriefing';

/** Lines starting with "-" or "•" become bullets; anything else stays a paragraph. */
function Lines({ text, color }: { text: string; color: string }) {
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const bullets = lines.filter((l) => /^[-•*]\s*/.test(l));
  if (bullets.length === 0) return <p className="text-sm whitespace-pre-line leading-relaxed" style={{ color }}>{text}</p>;
  return (
    <ul className="space-y-1">
      {lines.map((l, i) => (
        /^[-•*]\s*/.test(l)
          ? <li key={i} className="text-sm leading-relaxed flex gap-2" style={{ color }}><span className="opacity-50 shrink-0">•</span><span>{l.replace(/^[-•*]\s*/, '')}</span></li>
          : <li key={i} className="text-sm leading-relaxed" style={{ color }}>{l}</li>
      ))}
    </ul>
  );
}

const ICONS: Record<string, React.ComponentType<{ size?: number; style?: React.CSSProperties }>> = {
  summary: Sparkles, bookings: CalendarDays, sections: LayoutGrid, allergies: AlertTriangle, out_of_stock: PackageX, upselling: TrendingUp, notes: StickyNote,
};

const timeAgoShort = (iso: string) => new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).replace(/\s/g, '').toLowerCase();

export default function BriefingCard({ venueId, userId, isManager, tz }: { venueId: string; userId: string; isManager: boolean; tz?: string | null }) {
  const c = useAppColors();
  const { briefing, readers, hasRead, loading, error, draftWithAI, save, unpublish, markRead } = useBriefing(venueId, userId, isManager, tz);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Record<BriefingKey, string>>>({});
  const [busy, setBusy] = useState<null | 'ai' | 'save' | 'publish' | 'read'>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showReaders, setShowReaders] = useState(false);

  // Keep the form in step with what's saved whenever the editor isn't open.
  useEffect(() => {
    if (editing) return;
    const f: Partial<Record<BriefingKey, string>> = {};
    for (const { key } of BRIEFING_FIELDS) f[key] = (briefing?.[key] as string | null | undefined) ?? '';
    setForm(f);
  }, [briefing, editing]);

  const filled = BRIEFING_FIELDS.filter(({ key }) => (briefing?.[key] ?? '').toString().trim());
  const team = briefing?.facts?.team ?? [];
  const weather = briefing?.facts?.weather ?? null;
  const published = briefing?.status === 'published';
  const card = { background: c.bgElevated, border: `1px solid ${c.border}` };

  const run = async (kind: 'ai' | 'save' | 'publish', fn: () => Promise<{ error: string | null }>, ok: string) => {
    setBusy(kind); setMsg(null);
    const r = await fn();
    setBusy(null);
    setMsg(r.error ? { ok: false, text: r.error } : { ok: true, text: ok });
    return !r.error;
  };

  if (loading) return <section className="rounded-2xl p-5" style={card}><Loader2 size={16} className="animate-spin" style={{ color: c.textFaint }} /></section>;

  // ---- staff: nothing published yet ----
  if (!isManager && !published) {
    return (
      <section className="rounded-2xl p-5" style={card}>
        <Header c={c} />
        <p className="text-sm mt-2" style={{ color: c.textDim }}>{error ?? "Today's briefing isn't up yet. You'll get a notification when it is."}</p>
      </section>
    );
  }

  // ---- manager: editor ----
  if (isManager && editing) {
    return (
      <section className="rounded-2xl p-5 space-y-4" style={{ ...card, border: `1px solid ${c.accent}` }}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Header c={c} />
          <button onClick={() => void run('ai', async () => {
              // Keep what's been typed: save it first, then let the AI fill only the empty boxes.
              const s1 = await save(form); if (s1.error) return s1;
              const r = await draftWithAI(); if (!r.error) setEditing(false); return r;
            }, 'Done. The AI only filled the empty boxes.')}
            disabled={!!busy} className="h-9 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50" style={{ background: c.iconBg, color: c.text }}>
            {busy === 'ai' ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Fill empty boxes with AI
          </button>
        </div>
        <p className="text-xs" style={{ color: c.textDim }}>One point per line. Lines starting with "-" show as bullets. Who's on and the weather fill in automatically.</p>
        {BRIEFING_FIELDS.map(({ key, label, placeholder }) => (
          <label key={key} className="block">
            <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textFaint }}>{label}</span>
            <textarea value={form[key] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
              rows={3}
              className="mt-1 w-full px-3 py-2 rounded-xl text-sm outline-none resize-y" style={{ background: c.iconBg, color: c.text, border: `1px solid ${c.border}` }} />
          </label>
        ))}
        {msg && <p className="text-xs" style={{ color: msg.ok ? c.accentSoft : c.danger }}>{msg.text}</p>}
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setEditing(false)} disabled={!!busy} className="h-11 px-4 rounded-xl text-sm font-semibold" style={{ border: `1px solid ${c.borderStrong}`, color: c.text }}>Cancel</button>
          <button onClick={() => void run('save', () => save(form), published ? 'Saved. The team sees the update.' : 'Draft saved. Staff can\'t see it until you publish.').then((ok) => ok && setEditing(false))}
            disabled={!!busy} className="h-11 px-4 rounded-xl text-sm font-semibold disabled:opacity-50" style={{ background: c.iconBg, color: c.text }}>
            {busy === 'save' ? 'Saving…' : published ? 'Save changes' : 'Save draft'}
          </button>
          {!published && (
            <button onClick={() => void run('publish', () => save(form, true), 'Published. The team has been notified.').then((ok) => ok && setEditing(false))}
              disabled={!!busy || !Object.values(form).some((v) => v?.trim())} className="flex-1 min-w-[140px] h-11 rounded-xl text-sm font-semibold text-white disabled:opacity-50" style={{ background: c.accent }}>
              {busy === 'publish' ? 'Publishing…' : 'Publish to team'}
            </button>
          )}
        </div>
      </section>
    );
  }

  // ---- read view (staff and managers) ----
  return (
    <section className="rounded-2xl p-5 space-y-3" style={card}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Header c={c} />
        {isManager && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
              style={published ? { background: c.accentBg, color: c.accentSoft } : { background: 'rgba(232,160,32,0.14)', color: c.gold }}>
              {published ? 'Published' : briefing ? 'Draft' : 'Not started'}
            </span>
            <button onClick={() => { setMsg(null); setEditing(true); }} className="h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ background: c.iconBg, color: c.text }}>
              <Pencil size={12} /> Edit
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-sm" style={{ color: c.danger }}>{error}</p>}

      {isManager && !briefing && !error && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: c.textDim }}>Let OpsAlo draft today's briefing from the roster, weather, last night's check-ins and stock. You check it, add bookings, sections and allergies, then publish.</p>
          <button onClick={() => void run('ai', draftWithAI, 'Draft ready. Check it, add the rest, then publish.')} disabled={!!busy}
            className="h-11 px-4 rounded-xl text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-50" style={{ background: c.accent }}>
            {busy === 'ai' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy === 'ai' ? 'Drafting…' : 'Draft with AI'}
          </button>
        </div>
      )}

      {/* Weather + who's on */}
      {(weather || team.length > 0) && (
        <div className="grid gap-2 sm:grid-cols-[auto,1fr]">
          {weather && (
            <div className="rounded-xl px-3.5 py-2.5 flex items-center gap-3" style={{ background: c.iconBg }}>
              {weather.rain >= 50 ? <CloudRain size={22} style={{ color: c.textDim }} /> : /clear/.test(weather.label) ? <Sun size={22} style={{ color: c.gold }} /> : <Cloud size={22} style={{ color: c.textDim }} />}
              <div>
                <p className="text-sm font-semibold capitalize" style={{ color: c.text }}>{weather.label} · {weather.min}–{weather.max}°C</p>
                <p className="text-xs" style={{ color: weather.rain >= 50 ? c.gold : c.textDim }}>{weather.rain}% chance of rain</p>
              </div>
            </div>
          )}
          {team.length > 0 && (
            <div className="rounded-xl px-3.5 py-2.5 space-y-1.5" style={{ background: c.iconBg }}>
              <p className="text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5" style={{ color: c.textFaint }}><Users size={12} /> On today · {team.length}</p>
              {(['allday', 'day', 'evening'] as const).map((part) => {
                const ppl = team.filter((t) => t.part === part);
                if (!ppl.length) return null;
                return (
                  <div key={part} className="flex items-start gap-2 flex-wrap">
                    <span className="text-xs w-14 shrink-0 pt-0.5" style={{ color: c.textDim }}>{part === 'allday' ? 'All day' : part === 'day' ? 'Day' : 'Evening'}</span>
                    <div className="flex gap-1.5 flex-wrap flex-1">
                      {ppl.map((p) => (
                        <span key={p.name} className="text-xs px-2 py-0.5 rounded-full" style={{ background: c.bgElevated, color: c.text, border: `1px solid ${c.border}` }}>
                          {p.name.split(' ')[0]}{p.area ? <span style={{ color: c.textFaint }}> · {p.area.replace(/front of house/i, 'FOH').replace(/back of house/i, 'BOH')}</span> : null}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {filled.length > 0 && (
        <div className="space-y-2.5">
          {/* What to watch for, then the two things that can hurt a guest or the night */}
          {(['summary', 'allergies', 'out_of_stock'] as const).filter((k) => filled.some((f) => f.key === k)).map((key) => {
            const label = BRIEFING_FIELDS.find((f) => f.key === key)!.label;
            const Icon = ICONS[key];
            const tone = key === 'allergies' ? { bg: c.isDark ? 'rgba(248,113,113,0.08)' : 'rgba(220,38,38,0.05)', bd: c.isDark ? 'rgba(248,113,113,0.3)' : 'rgba(220,38,38,0.25)', fg: c.danger }
              : key === 'out_of_stock' ? { bg: 'rgba(232,160,32,0.08)', bd: 'rgba(232,160,32,0.3)', fg: c.gold }
              : { bg: 'transparent', bd: c.border, fg: c.accentSoft };
            return (
              <div key={key} className="rounded-xl px-3.5 py-3" style={{ background: tone.bg, border: `1px solid ${tone.bd}` }}>
                <p className="text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 mb-1.5" style={{ color: tone.fg }}><Icon size={12} /> {label}</p>
                <Lines text={String(briefing?.[key] ?? '')} color={c.text} />
              </div>
            );
          })}
          {/* Everything else, side by side on wider screens */}
          {filled.some((f) => !['summary', 'allergies', 'out_of_stock'].includes(f.key)) && (
            <div className="grid gap-2.5 sm:grid-cols-2">
              {filled.filter((f) => !['summary', 'allergies', 'out_of_stock'].includes(f.key)).map(({ key, label }) => {
                const Icon = ICONS[key];
                return (
                  <div key={key} className="rounded-xl px-3.5 py-3" style={{ border: `1px solid ${c.border}` }}>
                    <p className="text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 mb-1.5" style={{ color: c.textFaint }}><Icon size={12} /> {label}</p>
                    <Lines text={String(briefing?.[key] ?? '')} color={c.text} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {briefing && filled.length === 0 && <p className="text-sm" style={{ color: c.textDim }}>Empty draft. Tap Edit to fill it in.</p>}
      {isManager && briefing && !published && (
        <button onClick={() => void run('ai', draftWithAI, 'Redrafted. Your bookings, sections and allergies were kept.')} disabled={!!busy}
          className="text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50" style={{ color: c.textDim }}>
          {busy === 'ai' ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Redraft with AI (updates who's on, weather and "watch for")
        </button>
      )}

      {msg && <p className="text-xs" style={{ color: msg.ok ? c.accentSoft : c.danger }}>{msg.text}</p>}

      {isManager && briefing && !published && filled.length > 0 && (
        <button onClick={() => void run('publish', () => save(form, true), 'Published. The team has been notified.')} disabled={!!busy}
          className="w-full h-11 rounded-xl text-sm font-semibold text-white disabled:opacity-50" style={{ background: c.accent }}>
          {busy === 'publish' ? 'Publishing…' : 'Publish to team'}
        </button>
      )}

      {isManager && published && (
        <div className="flex items-center justify-between gap-2 flex-wrap pt-1" style={{ borderTop: `1px solid ${c.border}` }}>
          <button onClick={() => setShowReaders((v) => !v)} className="text-xs font-semibold flex items-center gap-1.5 pt-2" style={{ color: c.textDim }}>
            <Eye size={13} /> Read by {readers.length}{briefing?.published_at ? ` · published ${timeAgoShort(briefing.published_at)}` : ''}
          </button>
          <button onClick={() => { if (window.confirm('Hide the briefing from staff and go back to a draft?')) void unpublish(); }} className="text-xs pt-2" style={{ color: c.textFaint }}>Unpublish</button>
          {showReaders && (
            <p className="w-full text-xs" style={{ color: c.textDim }}>{readers.length ? readers.map((r) => `${r.name} (${timeAgoShort(r.read_at)})`).join(', ') : 'Nobody yet.'}</p>
          )}
        </div>
      )}

      {!isManager && published && (
        hasRead ? (
          <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: c.accentSoft }}><Check size={15} /> Got it</p>
        ) : (
          <button onClick={async () => { setBusy('read'); const r = await markRead(); setBusy(null); if (r.error) setMsg({ ok: false, text: r.error }); }} disabled={!!busy}
            className="w-full h-12 rounded-xl text-sm font-semibold text-white disabled:opacity-50" style={{ background: c.accent }}>
            {busy === 'read' ? 'Saving…' : 'Got it'}
          </button>
        )
      )}
    </section>
  );
}

function Header({ c }: { c: ReturnType<typeof useAppColors> }) {
  return (
    <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: c.text }}>
      <Megaphone size={16} style={{ color: c.accentSoft }} /> Today's briefing
    </h2>
  );
}
